<template>
  <v-container>
    <v-card class="mx-auto mt-5 pa-5" max-width="800" elevation="10" rounded="lg">
      <v-card-title class="text-h5 font-weight-bold text-center mb-4 text-pink-lighten-1">
        <v-icon color="pink-lighten-1" size="32" class="mr-2">mdi-account-plus</v-icon>
        เพิ่มนักศึกษาใหม่
      </v-card-title>
      
      <v-card-text>
        <v-form ref="form" v-model="valid" lazy-validation>
          <v-row>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="student.studentNum"
                :rules="[v => !!v || 'กรุณากรอกรหัสนักศึกษา']"
                label="รหัสนักศึกษา *"
                variant="outlined"
                required
                color="pink-lighten-1"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="student.enrollGroup"
                label="กลุ่มเรียน"
                variant="outlined"
                color="pink-lighten-1"
              ></v-text-field>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" md="4">
              <v-select
                v-model="student.prefix"
                :items="['นาย', 'นาง', 'นางสาว', 'จ.ส.อ.', 'ส.อ.', 'ส.ท.', 'ส.ต.', 'จ.ส.ต.', 'ส.ต.ต.']"
                :rules="[v => !!v || 'กรุณาเลือกคำนำหน้า']"
                label="คำนำหน้า *"
                variant="outlined"
                required
                color="pink-lighten-1"
              ></v-select>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="student.firstName"
                :rules="[v => !!v || 'กรุณากรอกชื่อ']"
                label="ชื่อ *"
                variant="outlined"
                required
                color="pink-lighten-1"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="student.lastName"
                label="นามสกุล"
                variant="outlined"
                color="pink-lighten-1"
              ></v-text-field>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" md="12">
              <v-text-field
                v-model="student.enName"
                label="ชื่อ-นามสกุล ภาษาอังกฤษ"
                hint="ตัวอย่าง: Somchai Jaidee"
                variant="outlined"
                color="pink-lighten-1"
              ></v-text-field>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="student.LineId"
                label="Line ID"
                variant="outlined"
                color="pink-lighten-1"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="student.PhoneNum"
                label="เบอร์โทรศัพท์"
                variant="outlined"
                color="pink-lighten-1"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="student.ContactEmail"
                label="Email ติดต่อ"
                variant="outlined"
                color="pink-lighten-1"
                type="email"
              ></v-text-field>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <v-card-actions class="justify-center mb-4">
        <v-btn
          color="pink-lighten-1"
          variant="elevated"
          size="large"
          class="px-8 text-white"
          :loading="loading"
          @click="submitForm"
        >
          <v-icon left class="mr-2">mdi-content-save</v-icon>
          บันทึกข้อมูล
        </v-btn>
        <v-btn
          color="grey-darken-1"
          variant="outlined"
          size="large"
          class="px-8 ml-4"
          @click="resetForm"
          :disabled="loading"
        >
          ล้างข้อมูล
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-container>
</template>

<script setup>
import { ref } from 'vue';
import axios from 'axios';
import { useAuthStore } from '@/stores/auth';
import { getCurrentInstance } from 'vue';

const { proxy } = getCurrentInstance();
const authStore = useAuthStore();
const form = ref(null);
const valid = ref(false);
const loading = ref(false);

const student = ref({
  studentNum: '',
  prefix: '',
  firstName: '',
  lastName: '',
  enName: '',
  enrollGroup: '',
  LineId: '',
  PhoneNum: '',
  ContactEmail: ''
});

const submitForm = async () => {
  const { valid } = await form.value.validate();
  
  if (!valid) return;

  loading.value = true;
  const token = authStore.getBackendJwt();

  try {
    const response = await axios.post('/api/AddStudent', student.value, {
      headers: {
        Authorization: `Bearer ${token}`
      },
      validateStatus: function (status) {
        return (status >= 200 && status < 300) || status === 400 || status === 409 || status === 401;
      },
    });

    if (response.status === 200) {
      await proxy.$swal({
        title: 'สำเร็จ!',
        text: 'เพิ่มข้อมูลนักศึกษาเรียบร้อยแล้ว',
        icon: 'success',
        confirmButtonColor: '#FF4081',
      });
      resetForm();
    } else {
      await proxy.$swal({
        title: 'เกิดข้อผิดพลาด',
        text: response.data.message || 'ไม่สามารถเพิ่มข้อมูลได้',
        icon: 'error',
        confirmButtonColor: '#FF4081',
      });
    }
  } catch (error) {
    console.error('Error adding student:', error);
    await proxy.$swal({
      title: 'ข้อผิดพลาดเครือข่าย',
      text: 'ไม่สามารถติดต่อเซิร์ฟเวอร์ได้',
      icon: 'error',
      confirmButtonColor: '#FF4081',
    });
  } finally {
    loading.value = false;
  }
};

const resetForm = () => {
  form.value.reset();
  student.value = {
    studentNum: '',
    prefix: '',
    firstName: '',
    lastName: '',
    enName: '',
    enrollGroup: '',
    LineId: '',
    PhoneNum: '',
    ContactEmail: ''
  };
};
</script>

<style scoped>
.v-card {
  border-radius: 16px;
}
</style>
